import {
  convert,
  type ConvertibleUnit,
  type FormatOptions,
  type Unit,
} from '@dynatrace-sdk/units';

import {
  type AnyNumericFormatter,
  type CategoricalFormatter,
  type FormatterWithPrecision,
  isFormatterOptions,
} from '../../core/types/formatter.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { isLinear } from '../types/guards.js';

interface AxisFormatterConfig {
  isRelative: boolean;
  type: string;
  formatter?: AnyNumericFormatter | CategoricalFormatter;
}

/**
 * Returns true when the axis should use nice unit-converted ticks.
 * Works with any axis shape that exposes isRelative, type and formatter.
 */
export function shouldUseNiceUnitTicks(
  config: AxisFormatterConfig | undefined,
): config is AxisFormatterConfig & {
  formatter: FormatOptions<Unit, ConvertibleUnit>;
} {
  if (!config || config.isRelative || !isLinear(config.type)) {
    return false;
  }
  const formatter = config.formatter as AnyNumericFormatter | undefined;
  return (
    isFormatterOptions(formatter) &&
    formatter.input !== undefined &&
    formatter.output !== undefined
  );
}

/**
 * Finds the best divisor (between 4 and 8) for dividing the total domain
 * into tick steps that produce the least remainder.
 */
export function getBestModulusDivisor(totalDomain: number): number {
  let bestDivisor = 4;
  let minMod = totalDomain % 4;

  for (let divisor = 4; divisor <= 8; divisor++) {
    const mod = totalDomain % divisor;
    if (mod === 0) {
      return divisor;
    }
    if (mod < minMod || (mod === minMod && divisor < bestDivisor)) {
      minMod = mod;
      bestDivisor = divisor;
    }
  }

  return bestDivisor;
}

/**
 * Converts domain endpoints to the target unit and returns them with the unit references.
 */
function convertDomainToTargetUnit(
  domain: [number, number],
  formatterOptions: FormatOptions<Unit, ConvertibleUnit>,
) {
  const inputUnit = formatterOptions.input as ConvertibleUnit;
  const outputUnit = formatterOptions.output as ConvertibleUnit;
  return {
    targetMin: convert(domain[0], inputUnit, outputUnit, true),
    targetMax: convert(domain[1], inputUnit, outputUnit, true),
    inputUnit,
    outputUnit,
  };
}

/**
 * Computes a nice step size for the given total domain and tick divisor.
 */
function computeNiceStep(totalDomain: number, tickDivisor: number): number {
  if (totalDomain < 1) {
    return 0;
  }

  const amount = totalDomain / Math.max(tickDivisor, 1);
  return amount < 10 ? Math.ceil(amount) : Math.floor(amount / 10) * 10;
}

/**
 * Generates tick values from 0 outward in both directions based on step size.
 */
export function generateTicks(
  domain: [number, number],
  step: number,
): number[] {
  if (step <= 0) {
    return [0];
  }
  const [min, max] = domain;
  const allTicks: number[] = [0];

  if (Math.sign(min) === Math.sign(max)) {
    const sign = Math.sign(max) || 1;
    const absMax = Math.max(Math.abs(min), Math.abs(max));
    const count = Math.floor(absMax / step);
    for (let i = 1; i <= count; i++) {
      allTicks.push(step * i * sign);
    }
  } else {
    const posCount = Math.floor(Math.abs(max) / step);
    const negCount = Math.floor(Math.abs(min) / step);
    for (let i = 1; i <= negCount; i++) {
      allTicks.push(-step * i);
    }
    for (let i = 1; i <= posCount; i++) {
      allTicks.push(step * i);
    }
  }

  return allTicks.sort((a, b) => a - b);
}

/**
 * Nices a scale domain in the target (output) unit space.
 *
 * Converts the domain to the output unit, computes a nice step,
 * rounds boundaries to step multiples, and converts back.
 *
 * Returns null when the step is 0 (sub-unit domain), signaling
 * the caller should fall back to d3 nicing.
 */
export function niceUnitDomain(
  domain: [number, number],
  formatterOptions: FormatOptions<Unit, ConvertibleUnit>,
  niceMin: boolean,
  niceMax: boolean,
): { min: number; max: number } | null {
  const { targetMin, targetMax, inputUnit, outputUnit } =
    convertDomainToTargetUnit(domain, formatterOptions);

  const totalDomain = Math.abs(targetMin) + Math.abs(targetMax);
  const step = computeNiceStep(totalDomain, getBestModulusDivisor(totalDomain));

  if (step === 0) {
    return null;
  }

  const nicedMin = niceMin ? Math.floor(targetMin / step) * step : targetMin;
  const nicedMax = niceMax ? Math.ceil(targetMax / step) * step : targetMax;

  return {
    min: convert(nicedMin, outputUnit, inputUnit, true),
    max: convert(nicedMax, outputUnit, inputUnit, true),
  };
}

export interface NiceUnitTicksResult {
  ticks: string[];
  formattedTicks: string[];
  numericTicks: number[];
}

/** Returned when the domain is too small for nice unit rounding; caller should fall back to default ticks. */
export type NiceUnitTicksResultOrNull = NiceUnitTicksResult | null;

/**
 * Computes "nice" round ticks for a unit-converted numerical axis.
 *
 * Uses convert() for precise domain conversion to the target unit,
 * then generates ticks from 0 outward (staying within domain boundaries),
 * and converts back to base unit for scale positioning.
 *
 * Requires explicit input and output units in formatterOptions.
 */
export function computeNiceUnitTicks(
  domain: [number, number],
  formatterOptions: FormatOptions<Unit, ConvertibleUnit>,
  availableSize: number,
  orientation: 'horizontal' | 'vertical',
  formatter: FormatterWithPrecision,
): NiceUnitTicksResultOrNull {
  const { targetMin, targetMax, inputUnit, outputUnit } =
    convertDomainToTargetUnit(domain, formatterOptions);

  if (targetMin === targetMax) {
    return {
      ticks: ['0'],
      formattedTicks: [formatter(0)],
      numericTicks: [0],
    };
  }

  // Total domain as sum of absolute values (handles mixed +/- domains)
  const totalDomain = Math.abs(targetMin) + Math.abs(targetMax);
  let tickDivisor = getBestModulusDivisor(totalDomain);

  // Prevent tick overcrowding in small charts
  const TICK_SPACING = 20;

  if (tickDivisor * TICK_SPACING >= availableSize) {
    tickDivisor =
      orientation === 'vertical'
        ? getVerticalNumTicks(availableSize)
        : getHorizontalNumTicks(availableSize);
  }

  const step = computeNiceStep(totalDomain, tickDivisor);

  // Domain is sub-unit (e.g. 40 bytes → 0.00004 MB) — nice rounding is
  // meaningless; let the caller fall back to default tick generation.
  if (step === 0) {
    return null;
  }

  // Generate ticks from 0 outward, staying within domain boundaries
  const targetTicks = generateTicks([targetMin, targetMax], step);

  // Convert ticks back to base unit for scale positioning
  const baseUnitTicks = targetTicks.map((tick) =>
    convert(tick, outputUnit, inputUnit, true),
  );

  // Format ticks using the provided formatter
  const formattedStrings = baseUnitTicks.map((tick) => formatter(tick));

  return {
    ticks: baseUnitTicks.map(String),
    formattedTicks: formattedStrings,
    numericTicks: baseUnitTicks,
  };
}
